"use client";
import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import houses from "../../assets/pics/houses.svg";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import { BiPencil } from "react-icons/bi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteItem } from "../../api/venue-api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { unpublishHotel } from "../../api/hotel-api";
import { useUser } from "../../context/user";
import { useSignedImages } from "../../hooks/useSignedImages";

const DetailComponent = ({ data, category, combineData, isCardOpen }) => {
  const checkAdmin = localStorage.getItem("checkAdmin");
  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null); // Ref for the popup
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Card images live in the private fmv-data S3 bucket, so the raw URLs 403.
  // Presign all of them up front (one batched request) and map each src below.
  const pickImages = (item, itemIndex) =>
    (itemIndex === 0 && combineData === "hotel" && item?.overview
      ? item?.photoVideo?.images
      : item?.imagesAndVideos?.images) || [];
  const allImages = (data || []).flatMap((item, i) => pickImages(item, i));
  const signedImages = useSignedImages(allImages);
  const signedSrcMap = new Map();
  allImages.forEach((url, i) => {
    if (!signedSrcMap.has(url)) signedSrcMap.set(url, signedImages[i] || url);
  });

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["venueData"]);
      toast.success("Item deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting item:", error.message);
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: unpublishHotel,
    onSuccess: () => {
      queryClient.invalidateQueries(["venueData"]);
      toast.success("Hotel unpublished successfully");
    },
    onError: (error) => {
      console.error("Error publishing Hotel:", error.message);
    },
  });

  // const handleDelete = async (id) => {
  //   await mutation.mutateAsync({ category, id });
  // };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setActivePopup(null);
      }
    };

    // Add event listener when the popup is open
    if (activePopup !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up event listener when the component unmounts or popup closes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activePopup]);

  const handleDelete = async (id, index) => {
    let updatedCategory = category;

    // Adjust the category based on conditions
    if (category === "Venue" && combineData === "hotel" && index === 0) {
      updatedCategory = "Hotel";
    } else if (category === "Venue" && combineData === "Restaurant") {
      updatedCategory = "Restaurant";
    } else {
      updatedCategory = "Venue";
    }

    await deleteMutation.mutateAsync({
      category: updatedCategory,
      id,
    });
  };
  const { setEditItem } = useUser();
  const handleEdit = (id, index, item) => {
    // if its first card than dont do anything but for the child cards geth the parent details
    if (index === 0) {
      setEditItem(null);
    } else {
      // storing parent card details of this tile in context
      setEditItem(data[0]); // Save the item in the context
    }

    if (
      category === "Venue" &&
      combineData === "hotel" &&
      index === 0 &&
      item?.overview
    ) {
      navigate(`/hostel-Listing-Form/Hotel/${id}`);
    } else if (category === "Venue" && combineData === "Restaurant") {
      navigate(`/venu-restaurant-Form/Restaurant/${id}`);
    } else if (category === "Venue") {
      navigate(`/create-Listing/${category}/${id}`);
    } else {
      navigate(`/venu-restaurant-Form/${category}/${id}`);
    }
  };

  const handlePublish = async (id, isPublished, index) => {
    let updatedCategory = category;

    // Adjust the category based on conditions
    if (category === "Venue" && combineData === "hotel" && index === 0) {
      updatedCategory = "Hotel";
    } else if (category === "Venue" && combineData === "Restaurant") {
      updatedCategory = "Restaurant";
    } else {
      updatedCategory = "Venue";
    }
    if (isPublished) {
      await unpublishMutation.mutateAsync({
        id,
        category: updatedCategory,
      });
    }
  };
  // Using an Array of Refs
  // The fix involved creating an array of refs, so each child slider gets its own unique reference:
  const sliderRefs = useRef([]);

  const imageSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    arrows: false,
  };

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    centerMode: false,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          centerMode: false,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          centerMode: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
        },
      },
    ],
  };
  // Function to handle next slide for a specific child slider
  const handleNext = (index) => {
    sliderRefs.current[index]?.slickNext();
  };

  // Function to handle previous slide for a specific child slider
  const handlePrev = (index) => {
    sliderRefs.current[index]?.slickPrev();
  };
  const handleTogglePopup = (index) => {
    setActivePopup((prevIndex) => (prevIndex === index ? null : index));
  };

  // Status classes with appropriate colors for each status
  const statusClasses = {
    Draft: "bg-[#EBEBEB] text-[#000000]", // Adjusted text color for visibility
    underReview: "bg-[#DFDFFF] text-[#4A49D9]",
    editsunderReview: "bg-[#DFDFFF] text-[#4A49D9]",
    live: "bg-[#EAF9F7] text-[#039F8D]",
    rejected: "bg-red-200 text-red-800",
    default: "bg-gray-200 text-gray-800",
  };

  const getStatusText = (status, itemStatus) => {
    switch (true) {
      case status.toLowerCase() === "pending" &&
        itemStatus.toLowerCase() === "pending":
        return "Draft";
      case status === "completed" &&
        ["pending", "Pending", "ReSubmitting"].includes(itemStatus):
        return "Under Review";
      case status === "completed" && itemStatus === "Edit Pending":
        return "Edits Under Review";
      case status === "completed" && itemStatus === "Approved":
        return "Live";
      case status === "completed" && itemStatus === "Rejected":
        return "Rejected";
      case status === "completed" && itemStatus === "unpublish":
        return "Unpublished";
      default:
        return "Draft";
    }
  };

  // Function to determine the class based on status and itemStatus
  const getStatusClass = (status, itemStatus) => {
    switch (true) {
      case status === "pending" && itemStatus === "pending":
        return statusClasses.Draft;
      case status === "completed" &&
        ["pending", "Pending", "ReSubmitting"].includes(itemStatus):
        return statusClasses.underReview;

      case status === "completed" && itemStatus === "Approved":
        return statusClasses.live;
      case status === "completed" && itemStatus === "Edit Pending":
        return statusClasses.editsunderReview;
      case status === "completed" && itemStatus === "Rejected":
        return statusClasses.rejected;
      default:
        return statusClasses.default;
    }
  };

  const CustomSkeleton = () => (
    <div className="w-full h-[150px] rounded-lg bg-gray-200 animate-pulse"></div>
  );
  return (
    <>
      {data && (
        <div className="grid grid-cols-1 w-full relative bg-gray-100 px-10 py-2">
          <Slider {...sliderSettings}>
            {data?.map((item, itemIndex) => {
              const type =
                itemIndex === 0 && combineData === "hotel" && item?.overview
                  ? "hotel"
                  : "venue";
              const images =
                type === "hotel"
                  ? item?.photoVideo?.images || []
                  : item?.imagesAndVideos?.images || [];

              const hasImages = images?.length > 0;

              return (
                <div
                  key={itemIndex}
                  className="px-2 lg:w-[250px] max-w-72 min-w-64"
                >
                  <div
                    className="bg-white rounded-lg gap-10"
                    style={{ margin: "10px" }}
                  >
                    <div className="relative ">
                      {/* child issue */}
                      <Slider
                        {...imageSettings}
                        // assigning a different ref to each slider using sliderRefs.current[itemIndex]
                        ref={(el) => (sliderRefs.current[itemIndex] = el)}
                        dots={false}
                      >
                        {hasImages ? (
                          images.map((e, imageIndex) => (
                            <div key={imageIndex} className="relative">
                              <img
                                src={signedSrcMap.get(e) || e}
                                alt=""
                                className="w-full h-[150px] rounded-lg object-cover"
                              />
                              {/* Custom Prev Arrow */}
                              <div
                                className="absolute left-2 h-fit bg-[#FFFFFF80] z-[1000] rounded-sm p-1 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                onClick={() => handlePrev(itemIndex)}
                              >
                                <SlArrowLeft className="text-[#000]" />
                              </div>
                              {/* Custom Next Arrow */}
                              <div
                                className="absolute right-2 h-fit bg-[#FFFFFF80] rounded-sm p-1 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                onClick={() => handleNext(itemIndex)}
                              >
                                <SlArrowRight className="text-[#000]" />
                              </div>
                            </div>
                          ))
                        ) : (
                          <CustomSkeleton />
                        )}
                      </Slider>

                      {activePopup === itemIndex && (
                        <div
                          ref={popupRef} // Attach ref to the popup
                          className="bg-white p-2 flex flex-col absolute right-5 top-36 rounded-lg gap-2 items-start z-50 shadow-md"
                        >
                          <p
                            onClick={() =>
                              handleEdit(item?._id, itemIndex, item)
                            }
                            className="py-2 w-full rounded-lg px-4 pr-10 hover:bg-lightGray cursor-pointer"
                          >
                            Edit
                          </p>
                          {item?.itemStatus.toLowerCase() !== "pending" &&
                            item?.itemStatus.toLowerCase() !== "unpublish" &&
                            item?.status !== "pending" &&
                            checkAdmin === "true" && (
                              <p
                                onClick={() =>
                                  handlePublish(
                                    item?._id,
                                    item?.itemStatus,
                                    itemIndex
                                  )
                                }
                                className="py-2 w-full rounded-lg px-4 pr-8 hover:bg-lightGray cursor-pointer"
                              >
                                Unpublish
                              </p>
                            )}{" "}
                          {type !== "hotel" && checkAdmin === "true" && (
                            <p
                              onClick={() => handleDelete(item?._id, itemIndex)}
                              className="py-2 hover:bg-lightGray w-full rounded-lg px-4 pr-10 cursor-pointer"
                            >
                              Delete
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-start gap-1 p-2 relative">
                      <div className="flex items-center w-full justify-between">
                        <h1 className="text-darkGray text-base font-medium truncate">
                          {type === "hotel"
                            ? item?.overview?.basicDetails?.hotelName
                            : `${item.basicDetails?.venueName}`}

                          {item?.basicDetails?.hotelName &&
                            item?.basicDetails?.hotelName?.trim() !== "" &&
                            `, ${item?.basicDetails?.hotelName}`}
                        </h1>
                        <div
                          className={`text-sm rounded-md p-2 ${getStatusClass(
                            item?.status,
                            item?.itemStatus
                          )}`}
                        >
                          {getStatusText(item?.status, item?.itemStatus)}
                        </div>
                      </div>
                      <p className="text-secondary text-sm">
                        {type === "hotel"
                          ? `${item?.overview?.locationDetails?.city}, ${item?.overview?.locationDetails?.area}`
                          : type === "venue"
                          ? `${
                              data[0]?.location?.city || item?.location?.city
                            }, ${
                              data[0]?.location?.area || item?.location?.area
                            }`
                          : `${item?.location?.city}, ${item?.location?.area}`}
                      </p>
                      <div className="flex items-center w-full justify-between gap-4">
                        {/* if no venuetype than make it invisible */}
                        {item?.additionalDetails?.venueSetting[0] ? (
                          <>
                            <div className="flex items-center gap-1 mt-3">
                              <img src={houses} alt="" />
                              <p className="text-secondary text-xs">
                                {item?.venueType?.venueRepresent
                                  ? `${item?.venueType?.venueRepresent} `
                                  : ""}
                                {item?.venueType?.venueRepresent && (
                                  <span
                                    style={{
                                      fontSize: "24px",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    &bull;{" "}
                                  </span>
                                )}
                                {item?.additionalDetails?.venueSetting
                                  ?.sort()
                                  .map((setting) => setting)
                                  .join(" & ")}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-1 mt-3 invisible">
                              <img src={houses} alt="" />
                              <p className="text-secondary text-sm">
                                {item?.venueType?.venueRepresent
                                  ? `${item?.venueType?.venueRepresent}`
                                  : ""}
                                {item?.additionalDetails?.venueSetting
                                  ?.sort()
                                  .map((setting) => setting)
                                  .join(" & ")}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      <div
                        className="b absolute bottom-2 right-2 p-1.5 rounded-full cursor-pointer"
                        onClick={() => handleTogglePopup(itemIndex)}
                      >
                        <BiPencil className="h-[20px] w-[20px]" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      )}
    </>
  );
};

export default DetailComponent;
